import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import {
  schedulingSeatMapSelector,
  selectedProductTariffHour,
  selectedTariffsSelector,
  setSeatMapAllocationStatus,
  seatMapAllocationStatusSelector,
} from '@src/stores/product';
import { useWidgetId } from '@src/core/IdProvider';
import { makeSelectedTariffs } from '@src/domains/tariffs';
import { createTariffHash, getSeatMapBestAllocationStatus } from '@src/common/seatMaps';
import { Scheduling } from '@src/core/buyerApiClient';
import useSelectedProductId from '@src/hooks/useSelectedProductId';

interface Props {
  scheduling?: Scheduling;
  timeMinutes?: number;
}

export const useAllocationStatus = (props: Props) => {
  const { scheduling, timeMinutes } = props;

  const prodId = useSelectedProductId();
  const widgetId = useWidgetId();
  const dispatch = useAppDispatch();
  const seatMap = useAppSelector(schedulingSeatMapSelector(scheduling?.id ?? '', timeMinutes));
  const tariffsPerTypeSelected = useAppSelector(selectedTariffsSelector(widgetId));
  const selectedHour = useAppSelector(selectedProductTariffHour(widgetId));
  const selectedTariffs = makeSelectedTariffs(tariffsPerTypeSelected, selectedHour?.minutes ?? null);
  const allocationStatus = useAppSelector(seatMapAllocationStatusSelector(widgetId));
  const selectedTariffsHash = createTariffHash(tariffsPerTypeSelected);

  useEffect(() => {
    if (!prodId) {
      return;
    }

    if (!seatMap || !scheduling) {
      dispatch(setSeatMapAllocationStatus(widgetId, { success: true, missingCapacity: 0, missingTariffs: 0 }));
      return;
    }

    let allocationStatus = { success: true, missingCapacity: 0, missingTariffs: 0 };

    if (selectedTariffs.length > 0) {
      allocationStatus = getSeatMapBestAllocationStatus(seatMap, selectedTariffs);
    }

    dispatch(setSeatMapAllocationStatus(widgetId, allocationStatus));
  }, [prodId, selectedTariffsHash, seatMap?.id, selectedHour?.minutes, widgetId, dispatch]);

  return useMemo(() => allocationStatus, [allocationStatus]);
};
